import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../components/layout/AdminLayout";
import { adminApi } from "../../api";
import ImageUploadField from "../../components/ImageUploadField";
import AdminSelect from "../../components/AdminSelect";
import ColorPicker from "../../components/ColorPicker";
import PasswordInput from "../../components/PasswordInput";
import EnvironmentSubTabs from "../../components/EnvironmentSubTabs";

export default function AdminVenueCreate() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeSection, setActiveSection] = useState("general");

  const [form, setForm] = useState({
    name: "",
    slug: "",
    logo_url: "",
    primary_color: "#3b82f6",
    accent_color: "#1e293b",
    background_color: "#0f172a",
    text_color: "#e2e8f0",
    button_radius: 8,
    font_family: "Inter",
    welcome_title: "Welcome",
    welcome_subtitle: "Join our membership program",
    cover_image_url: "",
    // Provider type + environment being configured (live | sandbox).
    // The create wizard sets up ONE environment; the other can be added
    // later from the venue's Provider tab.
    provider_type: "s7000",
    provider_env: "live",
    // S7000
    s7000_base_url: "http://system7000api.aristocratgaming.com:8444",
    s7000_reg_code: "",
    s7000_user_id: "",
    s7000_password: "",
    // IGT
    igt_srs_url: "",
    igt_mers_url: "",
    igt_username: "",
    igt_password: "",
    igt_external_id: "4434",
    igt_mac_key: "",
    igt_site_id: 1,
    igt_membership_type_id: 2,
    igt_term_id: 1,
    // SwiftPOS
    swiftpos_base_url: "https://api.swiftpos.com.au",
    swiftpos_integrator_name: "",
    swiftpos_integrator_key: "",
    swiftpos_client_id: "",
    swiftpos_customer_id: "",
    swiftpos_clerk_id: "0",
    swiftpos_classification_id: "",
    swiftpos_renewal_months: 12,
    // CircleScan
    circlescan_base_url: "",
    circlescan_api_key: "",
    circlescan_default_status: "",
    circlescan_default_type: "",
    circlescan_default_term_id: "",
    circlescan_renewal_months: 12,
    // Stripe
    stripe_secret_key: "",
    stripe_publishable_key: "",
    stripe_webhook_secret: "",
  });

  const set = (key) => (e) => {
    let val = e.target.value;
    if (key === "slug") val = val.toLowerCase().replace(/[^a-z0-9-]/g, "");
    setForm((f) => ({ ...f, [key]: val }));
  };

  // Env-aware column name: creds save to the live or sandbox_* column set
  // depending on which environment the admin is configuring.
  const col = (base) => form.provider_env === "sandbox" ? `sandbox_${base}` : base;

  const providerLabel =
    form.provider_type === "igt"      ? "IGT Config" :
    form.provider_type === "swiftpos" ? "SwiftPOS Config" :
    form.provider_type === "circlescan" ? "CircleScan Config" :
                                        "S7000 Config";
  const sections = [
    { id: "general", label: "General", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg> },
    { id: "branding", label: "Branding", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg> },
    { id: "provider", label: providerLabel, icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></svg> },
    { id: "stripe", label: "Stripe Config", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg> },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) {
      setError("Name and slug are required");
      setActiveSection("general");
      return;
    }

    setSaving(true);
    setError("");

    try {
      // 1. Create venue
      const venue = await adminApi.createVenue({
        name: form.name, slug: form.slug, logo_url: form.logo_url,
        primary_color: form.primary_color, accent_color: form.accent_color,
        background_color: form.background_color, text_color: form.text_color,
        button_radius: form.button_radius, font_family: form.font_family,
        welcome_title: form.welcome_title, welcome_subtitle: form.welcome_subtitle,
        cover_image_url: form.cover_image_url, provider_type: form.provider_type,
      });

      // 1b. Set the venue's environment (the create endpoint doesn't take
      //     signup_mode; live is the default so we only PUT for sandbox).
      if (form.provider_env === "sandbox") {
        await adminApi.updateVenue(venue.id, { signup_mode: "sandbox" });
      }

      // 2. Save provider config to the chosen environment's columns (col()).
      if (form.provider_type === "igt") {
        if (form.igt_srs_url && form.igt_username) {
          await adminApi.updateProvider(venue.id, {
            provider_type: "igt",
            [col("igt_srs_url")]: form.igt_srs_url,
            [col("igt_mers_url")]: form.igt_mers_url,
            [col("igt_username")]: form.igt_username,
            [col("igt_password")]: form.igt_password,
            [col("igt_external_id")]: form.igt_external_id,
            [col("igt_mac_key")]: form.igt_mac_key,
            [col("igt_site_id")]: form.igt_site_id,
            [col("igt_membership_type_id")]: form.igt_membership_type_id,
            [col("igt_term_id")]: form.igt_term_id,
          });
        }
      } else if (form.provider_type === "swiftpos") {
        if (form.swiftpos_integrator_name && form.swiftpos_integrator_key) {
          await adminApi.updateProvider(venue.id, {
            provider_type: "swiftpos",
            [col("swiftpos_base_url")]: form.swiftpos_base_url,
            [col("swiftpos_integrator_name")]: form.swiftpos_integrator_name,
            [col("swiftpos_integrator_key")]: form.swiftpos_integrator_key,
            [col("swiftpos_client_id")]: form.swiftpos_client_id,
            [col("swiftpos_customer_id")]: form.swiftpos_customer_id,
            [col("swiftpos_clerk_id")]: form.swiftpos_clerk_id,
            [col("swiftpos_classification_id")]: form.swiftpos_classification_id,
            [col("swiftpos_renewal_months")]: form.swiftpos_renewal_months,
          });
        }
      } else if (form.provider_type === "circlescan") {
        if (form.circlescan_base_url && form.circlescan_api_key) {
          await adminApi.updateProvider(venue.id, {
            provider_type: "circlescan",
            [col("circlescan_base_url")]: form.circlescan_base_url,
            [col("circlescan_api_key")]: form.circlescan_api_key,
            [col("circlescan_default_status")]: form.circlescan_default_status,
            [col("circlescan_default_type")]: form.circlescan_default_type,
            [col("circlescan_default_term_id")]: form.circlescan_default_term_id,
            [col("circlescan_renewal_months")]: form.circlescan_renewal_months,
          });
        }
      } else {
        if (form.s7000_reg_code && form.s7000_user_id) {
          await adminApi.updateProvider(venue.id, {
            provider_type: "s7000",
            [col("s7000_base_url")]: form.s7000_base_url,
            [col("s7000_reg_code")]: form.s7000_reg_code,
            [col("s7000_user_id")]: form.s7000_user_id,
            [col("s7000_password")]: form.s7000_password,
          });
        }
      }

      // 3. Save Stripe config if provided (to the chosen environment's columns).
      if (form.stripe_secret_key && form.stripe_publishable_key) {
        await adminApi.updateStripe(venue.id, {
          [col("stripe_secret_key")]: form.stripe_secret_key,
          [col("stripe_publishable_key")]: form.stripe_publishable_key,
          [col("stripe_webhook_secret")]: form.stripe_webhook_secret,
        });
      }

      navigate(`/admin/venues/${venue.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="venues-page-header">
        <div>
          <button className="btn btn-outline" onClick={() => navigate("/admin/venues")} style={{ marginBottom: "0.5rem" }}>← Back to Venues</button>
          <h1>Create Venue</h1>
          <p className="page-subtitle">Set up a new venue with all configuration in one place</p>
        </div>
      </div>

      {error && <div className="toast toast-error">{error}</div>}

      <div className="config-layout">
        {/* Section nav */}
        <div className="config-nav">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`config-nav-item ${activeSection === s.id ? "active" : ""}`}
              onClick={() => setActiveSection(s.id)}
              type="button"
            >
              {s.icon}
              <span>{s.label}</span>
            </button>
          ))}
        </div>

        {/* Form content */}
        <form className="config-panel" onSubmit={handleSubmit}>
          {activeSection === "general" && (
            <div className="form-section">
              <h2>General Information</h2>
              <p className="section-desc">Basic venue details used across the platform</p>
              <div className="form-grid-2">
                <div className="form-field">
                  <label>Venue Name <span className="required">*</span></label>
                  <input value={form.name} onChange={set("name")} placeholder="e.g. Riverside Club" required />
                </div>
                <div className="form-field">
                  <label>URL Slug <span className="required">*</span></label>
                  <div className="input-prefix-wrap">
                    <span className="input-prefix">/</span>
                    <input value={form.slug} onChange={set("slug")} placeholder="riverside-club" required />
                  </div>
                  <span className="field-hint">Public signup URL: /{form.slug || "..."}</span>
                </div>
              </div>
              <div className="form-field" style={{ marginTop: "1rem" }}>
                <label>Provider Type</label>
                <AdminSelect value={form.provider_type} onChange={(v) => setForm({ ...form, provider_type: v })} options={[{value:"s7000",label:"Aristocrat S7000"},{value:"igt",label:"IGT Advantage"},{value:"swiftpos",label:"SwiftPOS"},{value:"circlescan",label:"CircleScan"}]} />
                <span className="field-hint">Gaming / membership system this venue connects to for member registration</span>
              </div>
            </div>
          )}

          {activeSection === "branding" && (
            <div className="form-section">
              <h2>Branding & Appearance</h2>
              <p className="section-desc">Customize how the public signup page looks for this venue</p>

              {/* Logo & Cover */}
              <div className="form-grid-2">
                <ImageUploadField label="Logo" hint="Square or horizontal logo recommended" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} />
                <ImageUploadField label="Cover / Hero Image" hint="Wide landscape image for the signup header" value={form.cover_image_url} onChange={(v) => setForm({ ...form, cover_image_url: v })} />
              </div>

              {/* Colors */}
              <h3 style={{ fontSize: "0.95rem", fontWeight: 600, margin: "1.25rem 0 0.75rem", color: "var(--text)" }}>Colors</h3>
              <div className="form-grid-2">
                <div className="form-field">
                  <label>Primary Color</label>
                  <ColorPicker value={form.primary_color} onChange={(v) => setForm({ ...form, primary_color: v })} />
                  <span className="field-hint">Buttons, links, highlights</span>
                </div>
                <div className="form-field">
                  <label>Accent / Header Color</label>
                  <ColorPicker value={form.accent_color} onChange={(v) => setForm({ ...form, accent_color: v })} />
                  <span className="field-hint">Top bar, card headers</span>
                </div>
                <div className="form-field">
                  <label>Background Color</label>
                  <ColorPicker value={form.background_color} onChange={(v) => setForm({ ...form, background_color: v })} />
                  <span className="field-hint">Page background</span>
                </div>
                <div className="form-field">
                  <label>Text Color</label>
                  <ColorPicker value={form.text_color} onChange={(v) => setForm({ ...form, text_color: v })} />
                  <span className="field-hint">Body text on the signup page</span>
                </div>
              </div>

              {/* Typography & Shape */}
              <h3 style={{ fontSize: "0.95rem", fontWeight: 600, margin: "1.25rem 0 0.75rem", color: "var(--text)" }}>Typography & Style</h3>
              <div className="form-grid-2">
                <div className="form-field">
                  <label>Font Family</label>
                  <AdminSelect value={form.font_family} onChange={(v) => setForm({ ...form, font_family: v })} searchable searchPlaceholder="Search fonts..." options={[{value:"Inter",label:"Inter"},{value:"Roboto",label:"Roboto"},{value:"Open Sans",label:"Open Sans"},{value:"Poppins",label:"Poppins"},{value:"Montserrat",label:"Montserrat"},{value:"Lato",label:"Lato"},{value:"DM Sans",label:"DM Sans"},{value:"Nunito",label:"Nunito"},{value:"Source Sans 3",label:"Source Sans 3"}]} />
                </div>
                <div className="form-field">
                  <label>Button Radius — {form.button_radius}px</label>
                  <input type="range" min="0" max="24" value={form.button_radius} onChange={set("button_radius")} style={{ marginTop: "0.25rem" }} />
                  <span className="field-hint">0 = square buttons · 24 = fully rounded</span>
                </div>
              </div>

              {/* Welcome Text */}
              <h3 style={{ fontSize: "0.95rem", fontWeight: 600, margin: "1.25rem 0 0.75rem", color: "var(--text)" }}>Welcome Text</h3>
              <div className="form-grid-2">
                <div className="form-field">
                  <label>Title</label>
                  <input value={form.welcome_title} onChange={set("welcome_title")} placeholder="Welcome" />
                </div>
                <div className="form-field">
                  <label>Subtitle</label>
                  <input value={form.welcome_subtitle} onChange={set("welcome_subtitle")} placeholder="Join our membership program" />
                </div>
              </div>

              {/* Live Preview */}
              <h3 style={{ fontSize: "0.95rem", fontWeight: 600, margin: "1.5rem 0 0.75rem", color: "var(--text)" }}>Live Preview</h3>
              <div className="brand-preview-frame" style={{
                background: form.background_color,
                borderRadius: "var(--radius-lg)",
                border: "1px solid var(--border)",
                overflow: "hidden",
                fontFamily: form.font_family + ", sans-serif",
              }}>
                {/* Cover image */}
                {form.cover_image_url && (
                  <div style={{ height: 120, backgroundImage: `url(${form.cover_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                )}
                {/* Header bar */}
                <div style={{ background: form.accent_color, padding: "1rem 1.25rem", display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  {form.logo_url && <img src={form.logo_url} alt="" style={{ height: 32, borderRadius: 4 }} onError={(e) => e.target.style.display = "none"} />}
                  <strong style={{ color: "#fff", fontSize: "1rem" }}>{form.name || "Venue Name"}</strong>
                </div>
                {/* Body */}
                <div style={{ padding: "1.5rem 1.25rem", color: form.text_color }}>
                  <h2 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: "0.25rem", color: form.text_color }}>{form.welcome_title || "Welcome"}</h2>
                  <p style={{ fontSize: "0.9rem", opacity: 0.7, marginBottom: "1.25rem" }}>{form.welcome_subtitle || "Join our membership program"}</p>
                  <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: Number(form.button_radius) + 4 + "px", padding: "1rem", marginBottom: "1rem" }}>
                    <label style={{ fontSize: "0.8rem", opacity: 0.6, display: "block", marginBottom: "0.35rem" }}>Member Number</label>
                    <div style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: form.button_radius + "px", padding: "0.55rem 0.75rem", fontSize: "0.85rem", color: form.text_color, opacity: 0.4 }}>Enter your member number…</div>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button type="button" style={{ background: form.primary_color, border: "none", color: "#fff", padding: "0.55rem 1.25rem", borderRadius: form.button_radius + "px", fontSize: "0.85rem", fontWeight: 600, cursor: "default" }}>Continue</button>
                    <button type="button" style={{ background: "transparent", border: `1px solid ${form.primary_color}`, color: form.primary_color, padding: "0.55rem 1.25rem", borderRadius: form.button_radius + "px", fontSize: "0.85rem", fontWeight: 500, cursor: "default" }}>Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === "provider" && (
            <div className="form-section">
              <h2>{form.provider_type === "igt" ? "IGT Advantage Configuration" : form.provider_type === "swiftpos" ? "SwiftPOS Configuration" : form.provider_type === "circlescan" ? "CircleScan Configuration" : "Aristocrat S7000 Configuration"}</h2>
              <p className="section-desc">{form.provider_type === "igt" ? "IGT Club Services API credentials" : form.provider_type === "swiftpos" ? "SwiftPOS Cloud API credentials" : form.provider_type === "circlescan" ? "CircleScan access-control API credentials" : "Credentials for the S7000 Gaming System API"}</p>

              {/* Which environment these credentials belong to. The venue is
                  created in this mode; the other environment can be added later
                  from the venue's Provider tab. */}
              <EnvironmentSubTabs value={form.provider_env} onChange={(v) => setForm({ ...form, provider_env: v })} />
              <p className="field-hint" style={{ marginTop: "-0.25rem", marginBottom: "1rem" }}>
                Configuring the <strong>{form.provider_env === "sandbox" ? "Sandbox" : "Live"}</strong> environment — the venue will be created in {form.provider_env === "sandbox" ? "sandbox" : "live"} mode.
              </p>

              {form.provider_type === "s7000" && (
                <>
                  <div className="form-field">
                    <label>API Base URL</label>
                    <input value={form.s7000_base_url} onChange={set("s7000_base_url")} placeholder="http://system7000api.aristocratgaming.com:8444" />
                  </div>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Registration Code</label>
                      <input value={form.s7000_reg_code} onChange={set("s7000_reg_code")} placeholder="e.g. 32BABE47FA8604F7" />
                    </div>
                    <div className="form-field">
                      <label>User ID</label>
                      <input value={form.s7000_user_id} onChange={set("s7000_user_id")} placeholder="e.g. CIR0NEWV" />
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Password / Credentials</label>
                    <PasswordInput value={form.s7000_password} onChange={set("s7000_password")} placeholder="S7000 web password" />
                  </div>
                  <div className="info-box">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                    <span>These credentials connect to the venue's S7000 system for member registration and lookup. You can configure this later.</span>
                  </div>
                </>
              )}

              {form.provider_type === "igt" && (
                <>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>SRS URL (Security)</label>
                      <input value={form.igt_srs_url} onChange={set("igt_srs_url")} placeholder="http://igtreverseproxy...:8011/club/1.0.0" />
                    </div>
                    <div className="form-field">
                      <label>MeRS URL (Membership)</label>
                      <input value={form.igt_mers_url} onChange={set("igt_mers_url")} placeholder="http://igtreverseproxy...:8091/club/1.0.0/members" />
                    </div>
                  </div>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Username</label>
                      <input value={form.igt_username} onChange={set("igt_username")} />
                    </div>
                    <div className="form-field">
                      <label>Password</label>
                      <PasswordInput value={form.igt_password} onChange={set("igt_password")} />
                    </div>
                  </div>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>External ID</label>
                      <input value={form.igt_external_id} onChange={set("igt_external_id")} />
                    </div>
                    <div className="form-field">
                      <label>HMAC Key (Base64)</label>
                      <PasswordInput value={form.igt_mac_key} onChange={set("igt_mac_key")} />
                    </div>
                  </div>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Site ID</label>
                      <input type="number" value={form.igt_site_id} onChange={(e) => setForm({ ...form, igt_site_id: parseInt(e.target.value) || 1 })} />
                    </div>
                    <div className="form-field">
                      <label>Membership Type</label>
                      <AdminSelect value={form.igt_membership_type_id} onChange={(v) => setForm({ ...form, igt_membership_type_id: parseInt(v) })} options={[{value:1,label:"1 — Life Members"},{value:2,label:"2 — Club RSL Full"},{value:3,label:"3 — Visitor"}]} />
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Term</label>
                    <AdminSelect value={form.igt_term_id} onChange={(v) => setForm({ ...form, igt_term_id: parseInt(v) })} options={[{value:1,label:"1 — 1 Year"},{value:2,label:"2 — 3 Year"},{value:3,label:"3 — Lifetime"},{value:4,label:"4 — Visitor"},{value:5,label:"5 — 20 Year"}]} />
                  </div>
                  <div className="info-box">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                    <span>IGT sessions expire every 90s. The system handles keepalive automatically. You can configure this later.</span>
                  </div>
                </>
              )}

              {form.provider_type === "swiftpos" && (
                <>
                  <div className="form-field">
                    <label>API Base URL</label>
                    <input value={form.swiftpos_base_url} onChange={set("swiftpos_base_url")} placeholder="https://api.swiftpos.com.au" />
                  </div>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Integrator Name (Signature)</label>
                      <input value={form.swiftpos_integrator_name} onChange={set("swiftpos_integrator_name")} placeholder="e.g. Swift-Circles" />
                    </div>
                    <div className="form-field">
                      <label>Integrator Key (Identity)</label>
                      <PasswordInput value={form.swiftpos_integrator_key} onChange={set("swiftpos_integrator_key")} />
                    </div>
                  </div>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Client ID</label>
                      <input value={form.swiftpos_client_id} onChange={set("swiftpos_client_id")} placeholder="e.g. 673237" />
                    </div>
                    <div className="form-field">
                      <label>Customer ID</label>
                      <input value={form.swiftpos_customer_id} onChange={set("swiftpos_customer_id")} placeholder="e.g. 80226678" />
                    </div>
                  </div>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Clerk ID (usually 0)</label>
                      <input value={form.swiftpos_clerk_id} onChange={set("swiftpos_clerk_id")} placeholder="0" />
                    </div>
                    <div className="form-field">
                      <label>Default Classification ID</label>
                      <input type="number" min="0" value={form.swiftpos_classification_id} onChange={(e) => { const n = parseInt(e.target.value, 10); setForm({ ...form, swiftpos_classification_id: Number.isNaN(n) ? "" : n }); }} placeholder="e.g. 1" />
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Renewal Term (months)</label>
                    <input type="number" min="1" value={form.swiftpos_renewal_months} onChange={(e) => { const n = parseInt(e.target.value, 10); setForm({ ...form, swiftpos_renewal_months: Number.isNaN(n) ? "" : n }); }} placeholder="12" />
                  </div>
                  <div className="info-box">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                    <span>SwiftPOS assigns the member number on creation; membership state is the classification + renewal date. After creating the venue, run <strong>Test Connection</strong> on the Provider tab to confirm the classification exists. You can configure this later.</span>
                  </div>
                </>
              )}

              {form.provider_type === "circlescan" && (
                <>
                  <div className="form-field">
                    <label>API Base URL</label>
                    <input value={form.circlescan_base_url} onChange={set("circlescan_base_url")} placeholder="https://…azurewebsites.net" />
                  </div>
                  <div className="form-field">
                    <label>API Key</label>
                    <PasswordInput value={form.circlescan_api_key} onChange={set("circlescan_api_key")} placeholder="sent as Authorization: ApiKey" />
                  </div>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Default Status (code)</label>
                      <input value={form.circlescan_default_status} onChange={set("circlescan_default_status")} placeholder="e.g. FINANCIAL" />
                    </div>
                    <div className="form-field">
                      <label>Default Type (code)</label>
                      <input value={form.circlescan_default_type} onChange={set("circlescan_default_type")} placeholder="e.g. FULL" />
                    </div>
                  </div>
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Default Term ID</label>
                      <input type="number" min="0" value={form.circlescan_default_term_id} onChange={(e) => { const n = parseInt(e.target.value, 10); setForm({ ...form, circlescan_default_term_id: Number.isNaN(n) ? "" : n }); }} placeholder="e.g. 1" />
                    </div>
                    <div className="form-field">
                      <label>Renewal Term (months)</label>
                      <input type="number" min="1" value={form.circlescan_renewal_months} onChange={(e) => { const n = parseInt(e.target.value, 10); setForm({ ...form, circlescan_renewal_months: Number.isNaN(n) ? "" : n }); }} placeholder="12" />
                    </div>
                  </div>
                  <div className="info-box">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                    <span>CircleScan uses a static API key (Authorization: ApiKey). It assigns the member number on creation and validates ban / self-exclusion at signup. Status/type/term defaults populate as dropdowns after a reference-data sync. You can configure this later.</span>
                  </div>
                </>
              )}
            </div>
          )}

          {activeSection === "stripe" && (
            <div className="form-section">
              <h2>Stripe Payment Configuration</h2>
              <p className="section-desc">Connect this venue's Stripe account. Payments are verified server-side — no webhook setup required.</p>
              <div className="form-field">
                <label>Publishable Key</label>
                <span className="field-hint">Stripe Dashboard → Developers → API keys (starts with pk_) · saved to the {form.provider_env === "sandbox" ? "Sandbox" : "Live"} environment</span>
                <input value={form.stripe_publishable_key} onChange={set("stripe_publishable_key")} placeholder={form.provider_env === "sandbox" ? "pk_test_..." : "pk_live_..."} />
              </div>
              <div className="form-field">
                <label>Secret Key</label>
                <span className="field-hint">Stripe Dashboard → Developers → API keys (starts with sk_)</span>
                <PasswordInput value={form.stripe_secret_key} onChange={set("stripe_secret_key")} placeholder={form.provider_env === "sandbox" ? "sk_test_..." : "sk_live_..."} />
              </div>
              <div className="info-box">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                <span>Membership products and pricing are read directly from your Stripe account. Create products in Stripe Dashboard first.</span>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={() => navigate("/admin/venues")}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Creating..." : "Create Venue"}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}
